const { getElements } = window.BAO.Utils

class ResultsGrid extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.eventBus = window.theme.nosto.eventBus
    this.events = window.theme.results.events
    this.cardCache = new Map()
    this.pageUrl = window.location.pathname
    this.template = this.pageUrl.includes('/collections/')
      ? 'collection'
      : 'search'
  }

  connectedCallback () {
    super.connectedCallback()
    this.handleProductsUpdated = this.handleProductsUpdated.bind(this)

    this.eventBus.subscribe(
      window.theme.results.events.STATE.PRODUCTS_UPDATED,
      this.handleProductsUpdated,
    )
  }

  disconnectedCallback () {
    this.eventBus.unsubscribe(
      window.theme.results.events.STATE.PRODUCTS_UPDATED,
      this.handleProductsUpdated,
    )
  }

  setupListeners () {
    super.setupListeners()

    this.handleClick.bind(this)
    this.listeners.add(this, 'click', this.handleClick)
  }

  async handleProductsUpdated ({ products, interaction, state }) {
    this.updateView(products)

    if (
      interaction === 'initial' ||
      interaction === 'filter' ||
      interaction === 'sort'
    ) {
      const itemEls = getElements('[data-results-grid-el="item"]', {
        context: this.els.resultsProducts.element,
      })

      const htmlList = await this.renderProducts(products)

      if (interaction === 'initial') {
        this.promoteFirstRowImages(htmlList)
      }

      itemEls.forEach((itemEl) => {
        if (itemEl.dataset.type === 'promo') return
        itemEl.remove()
      })

      htmlList.forEach((listEl) => {
        this.els.resultsProducts.element.appendChild(listEl)
      })
    }

    if (
      interaction === 'pagination' ||
      interaction === 'paginationNext' ||
      interaction === 'paginationPrevious'
    ) {
      const fragment = document.createDocumentFragment()
      const htmlList = await this.renderProducts(products)

      htmlList.forEach((listEl) => {
        fragment.appendChild(listEl)
      })

      if (interaction === 'paginationPrevious') {
        this.els.resultsProducts.element.prepend(fragment)
      } else {
        this.els.resultsProducts.element.appendChild(fragment)
      }
    }

    const hidePromos =
      state.activeFacets.length || state.pagination.showPreviousButton

    if (!hidePromos) {
      this.handlePromoCards()
    }

    this.els.resultsProducts.element.classList.toggle(
      'prd-List_Items-hidePromos',
      hidePromos,
    )

    if (window.BAO.MediaBreakpoints.isSm) return

    if (interaction === 'filter' || interaction === 'sort') {
      this.els.productGrid.element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  updateView (products) {
    if (products.length) {
      this.els.noResultsView.element.setAttribute('hidden', '')
      this.els.resultsView.element.removeAttribute('hidden')
      return
    }

    this.els.noResultsView.element.removeAttribute('hidden')
    this.els.resultsView.element.setAttribute('hidden', '')
  }

  renderProducts (products) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const htmlList = await Promise.all(
        products.map(async (product) => {
          if (!product.url) return ''
          let url = new URL(product.url)

          if (this.template === 'collection') {
            url.pathname = this.pageUrl + url.pathname
          }

          url = url.pathname

          if (!url.startsWith(window.Shopify.routes.root)) {
            url = `${window.Shopify.routes.root}${url}`
          }

          let articleHtml = this.cardCache.get(product.url)

          if (!articleHtml) {
            try {
              this.card = 'nosto-product-card'

              const sectionHtml = await window.BAO.sectionRenderer.fetchSingle(
                this.card,
                url,
              )

              const sectionDocument =
                window.BAO.sectionRenderer.convertToDOM(sectionHtml)
              const articleElement = sectionDocument.querySelector('.prd-Card')
              articleElement?.setAttribute('data-nosto-type', this.nostoType)
              articleHtml = articleElement ? articleElement.outerHTML : ''

              this.cardCache.set(url, articleHtml)
            } catch (error) {
              console.error('Failed to load product card', error)
              return ''
            }
          }

          if (!articleHtml) return ''

          const listEl = document.createElement('li')
          listEl.id = `product-${product.productId}`
          listEl.classList.add('prd-List_Item')
          listEl.setAttribute('data-results-grid-el', 'item')
          // Needed for collection scroll position
          listEl.setAttribute('data-collection-filters-el', 'item')
          listEl.setAttribute('data-type', 'product')

          listEl.innerHTML = articleHtml

          return listEl
        }),
      )

      resolve(htmlList.filter(Boolean))
    })
  }

  /**
   *
   * @param {HTMLElement[]} listEls - The cards, in grid order, before they're in the document
   *
   * @returns {void}
   */
  promoteFirstRowImages (listEls) {
    listEls.slice(0, 4).forEach((listEl) => {
      const imageEl = listEl.querySelector(
        '.prd-Card_Media:not(.prd-Card_Media-hover) .rsp-Image_Image',
      )

      if (!imageEl) return

      imageEl.setAttribute(
        'sizes',
        '(max-width: 767px) 50vw, (min-width: 768px) 30vw',
      )
      imageEl.setAttribute('loading', 'eager')
      imageEl.setAttribute('fetchpriority', 'high')
    })
  }

  handlePromoCards () {
    const itemEls = getElements('[data-results-grid-el="item"]', {
      context: this.els.resultsProducts.element,
    })

    const promoCards = itemEls.filter(
      (itemEl) => itemEl.dataset.type === 'promo',
    )

    if (!promoCards.length) return

    // Add correct index to all product cards
    let productIndex = 1

    itemEls.forEach((itemEl) => {
      if (itemEl.dataset.type === 'promo') return
      itemEl.dataset.index = productIndex
      productIndex += 1
    })

    // Go through each promo card and move it before corresponding product card
    promoCards.forEach((promoCardEl) => {
      const index = promoCardEl.dataset.index

      const productCard = itemEls.find((itemEl) => {
        return (
          itemEl.dataset.type === 'product' && itemEl.dataset.index === index
        )
      })

      if (!productCard) {
        promoCardEl.setAttribute('hidden', '')
        return
      }

      promoCardEl.removeAttribute('hidden')
      productCard.insertAdjacentElement('beforebegin', promoCardEl)
    })
  }

  handleClick (event) {
    const productCardSelector = '[data-results-grid-el="item"]'
    const productCardEl = event.target.closest(productCardSelector)

    if (!productCardEl) return
    this.handleProductCardClick(event)
  }

  handleProductCardClick (event) {
    const productCard = event.target.closest('.prd-Card')
    const url = productCard?.dataset.productUrl || false

    if (!productCard || !url) return

    this.eventBus.publish(this.events.TRACKING.PRODUCT_CLICKED, {
      productId: productCard.dataset.productId,
      productUrl: productCard.dataset.productUrl,
      type: this.nostoType,
    })
  }

  get nostoType () {
    return this.dataset.nostoType || 'category'
  }
}

if (!customElements.get('results-grid')) {
  customElements.define('results-grid', ResultsGrid)
}
